import { QueryClient, useQueryClient } from "@tanstack/react-query";

import { LaboratoryResearch } from "@nens/brostar/openapi-client";

import { getApi } from "@/api/api-configuration";
import { GarLabData } from "@/helpers/convert-gar-to-task";

import { fetchAllPages } from "./helpers/fetch-all-pages";

const uploadApi = getApi();

// ─── INVALIDATION ─────────────────────────────────────────────────────────────
const fetchLabResearches = async (queryClient: QueryClient, garUuid: string) => {
  return queryClient
    .fetchQuery({
      queryKey: ["gar-lab-research-all-pages", garUuid],
      queryFn: () =>
        fetchAllPages(({ page }) =>
          uploadApi.apiGarLaboratoryresearchesList({ gar: garUuid, page })
        ),
    })
    .then((r) => r.results);
};

const fetchAnalysisProcesses = async (queryClient: QueryClient, labResearchUuid: string) => {
  return queryClient
    .fetchQuery({
      queryKey: ["gar-analysis-process-all-pages", labResearchUuid],
      queryFn: () =>
        fetchAllPages(({ page }) =>
          uploadApi.apiGarAnalysisprocessesList({
            laboratoryResearch: labResearchUuid,
            page,
          })
        ),
    })
    .then((r) => r.results);
};

const fetchAnalyses = async (queryClient: QueryClient, processUuid: string) => {
  return queryClient
    .fetchQuery({
      queryKey: ["gar-analysis-all-pages", processUuid],
      queryFn: () =>
        fetchAllPages(({ page }) =>
          uploadApi.apiGarAnalysesList({ analysisProcess: processUuid, page })
        ),
    })
    .then((r) => r.results);
};

const fetchProcessesWithAnalyses = async (
  queryClient: QueryClient,
  labResearch: LaboratoryResearch
) => {
  const analysisProcesses = await fetchAnalysisProcesses(queryClient, labResearch.uuid);

  const processes = await Promise.all(
    analysisProcesses.map(async (process) => {
      const analyses = await fetchAnalyses(queryClient, process.uuid);
      return { process, analyses };
    })
  );

  return { labResearch, processes };
};

export const useGarLaboratoryAnalysisQueryDirectly = () => {
  const queryClient = useQueryClient();

  return async (garUuid: string): Promise<GarLabData> => {
    const labResearches = await fetchLabResearches(queryClient, garUuid);

    return Promise.all(
      labResearches.map((labResearch) => fetchProcessesWithAnalyses(queryClient, labResearch))
    );
  };
};
